import { revokeCurrentStaffSession } from '../staffSession';
import { getStaffSessionToken } from '../staffSession';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { toast } from 'react-toastify';

import logo from '../../../assets/images/farmstream_logo.png';
import { useAppDispatch } from '../../../store';
import { authSelector, setAuthToken, clearToken } from '../state/authSlice';
import {
  useTeamSearchUsersMutation,
  useTeamSwitchUserMutation,
  useGenerateShareLinkMutation
} from '../api/authApi';
import { api } from '../../../api/api';

type SearchResult = {
  email: string;
  matchedVia: 'email' | 'iccid' | 'stripe';
  matchedValue: string;
};

/**
 * Landing page shown after the admin team signs in via Google Workspace
 * SSO. The admin's @farmstream.co.uk identity does not exist in the
 * customer activations table, so we cannot drop them on /dashboard
 * directly. Instead we ask them to look up a customer (by email, ICCID,
 * or Stripe ID) and then swap their session into that customer's view.
 */
export const AdminSelectUser = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { isAdmin, token } = useSelector(authSelector);

  const [teamSearchUsers, { isLoading: isSearching }] = useTeamSearchUsersMutation();
  const [teamSwitchUser, { isLoading: isSwitching }] = useTeamSwitchUserMutation();
  const [generateShareLink, { isLoading: isGeneratingLink }] = useGenerateShareLinkMutation();

  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [linkingEmail, setLinkingEmail] = useState<string | null>(null);

  const staffSession = useMemo(
    () => getStaffSessionToken() || '',
    []
  );

  // If we somehow land here without an admin session, bounce to team-login.
  useEffect(() => {
    if (!token || !isAdmin || !staffSession) {
      navigate('/team-login', { replace: true });
    }
  }, [token, isAdmin, staffSession, navigate]);

  // Debounced search
  useEffect(() => {
    const trimmed = query.trim();
    if (trimmed.length < 2) {
      setResults([]);
      return;
    }
    if (!staffSession) return;
    const handle = setTimeout(async () => {
      try {
        const res = await teamSearchUsers({
          query: trimmed,

        }).unwrap();
        setResults(res.results || []);
      } catch {
        setResults([]);
      }
    }, 250);
    return () => clearTimeout(handle);
  }, [query, staffSession, teamSearchUsers]);

  const handleSelect = async (email: string) => {
    if (!staffSession) {
      toast('Admin session expired, please sign in again', { type: 'error' });
      navigate('/team-login', { replace: true });
      return;
    }
    try {
      const res = await teamSwitchUser({ email }).unwrap();
      if (res?.token) {
        dispatch(setAuthToken(res.token));
        dispatch(api.util.resetApiState());
        toast(`Switched to ${email}`, { type: 'success' });
        navigate('/dashboard');
      }
    } catch (err) {
      const message =
        (err as { data?: { message?: string } })?.data?.message ||
        'Failed to switch user';
      toast(message, { type: 'error' });
    }
  };

  const handleSignOut = async () => {
    await revokeCurrentStaffSession();
    dispatch(clearToken());
    navigate('/team-login', { replace: true });
  };

  const handleCopyShareLink = async (email: string) => {
    if (!staffSession) {
      toast('Admin session expired, please sign in again', { type: 'error' });
      navigate('/team-login', { replace: true });
      return;
    }
    setLinkingEmail(email);
    try {
      const res = await generateShareLink({ email }).unwrap();
      if (res?.magicLink) {
        await navigator.clipboard.writeText(res.magicLink);
        toast(`Share link copied for ${email}`, { type: 'success' });
      } else {
        toast('No link returned by server', { type: 'error' });
      }
    } catch (err) {
      const message =
        (err as { data?: { message?: string } })?.data?.message ||
        'Failed to generate share link';
      toast(message, { type: 'error' });
    } finally {
      setLinkingEmail(null);
    }
  };

  return (
    <Background>
      <StyledImg src={logo} alt="Farmstream logo" />
      <Container>
        <a href="/team/access">Staff access &amp; sessions</a>
        <Title>Select a customer</Title>
        <button type="button" onClick={() => navigate('/team/migrations')}>Camera migrations →</button>
        <button type="button" onClick={() => navigate('/team/camera-diagnostics')}>Night image diagnostics →</button>
        <Subtitle>
          Search by email, ICCID, or Stripe customer ID to load their account.
        </Subtitle>
        <SearchInput
          value={query}
          autoFocus
          placeholder="Search by email, ICCID or Stripe ID…"
          onChange={(e) => setQuery(e.target.value)}
        />
        <ResultsBox>
          {query.trim().length < 2 && (
            <EmptyState>Enter at least 2 characters to search.</EmptyState>
          )}
          {query.trim().length >= 2 && isSearching && (
            <EmptyState>Searching…</EmptyState>
          )}
          {query.trim().length >= 2 && !isSearching && results.length === 0 && (
            <EmptyState>No matches</EmptyState>
          )}
          {!isSearching &&
            results.map((r) => {
              const viaLabel =
                r.matchedVia === 'iccid'
                  ? `via ICCID ${r.matchedValue}`
                  : r.matchedVia === 'stripe'
                    ? `via Stripe ${r.matchedValue}`
                    : null;
              const isLinkingThis = isGeneratingLink && linkingEmail === r.email;
              return (
                <ResultRow key={`${r.email}-${r.matchedVia}`}>
                  <ResultText>
                    <ResultEmail>{r.email}</ResultEmail>
                    {viaLabel && <ResultHint>{viaLabel}</ResultHint>}
                  </ResultText>
                  <ResultActions>
                    <ShareLinkBtn
                      type="button"
                      disabled={isSwitching || isLinkingThis}
                      onClick={() => handleCopyShareLink(r.email)}
                      title="Generate & copy a 24h magic link"
                    >
                      {isLinkingThis ? 'Copying…' : 'Copy share link'}
                    </ShareLinkBtn>
                    <OpenBtn
                      type="button"
                      disabled={isSwitching || isLinkingThis}
                      onClick={() => handleSelect(r.email)}
                    >
                      Open
                    </OpenBtn>
                  </ResultActions>
                </ResultRow>
              );
            })}
        </ResultsBox>
        <SignOutBtn type="button" onClick={handleSignOut}>
          Sign out of admin
        </SignOutBtn>
      </Container>
    </Background>
  );
};

const Background = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100dvh;
  background-color: #f5f5f5;
  padding: 1rem;
`;

const StyledImg = styled.img`
  max-width: 15rem;
  margin-bottom: 2rem;

  @media (max-width: 500px) {
    max-width: 9rem;
  }
`;

const Container = styled.div`
  background-color: white;
  padding: 2rem;
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  width: 100%;
  max-width: 480px;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const Title = styled.h2`
  text-align: center;
  margin: 0;
`;

const Subtitle = styled.p`
  text-align: center;
  color: #6b7280;
  font-size: 0.85rem;
  margin: 0 0 0.5rem;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.6rem 0.75rem;
  font-size: 0.95rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  background: #f9fafb;
  box-sizing: border-box;
  &:focus {
    outline: none;
    border-color: #29a8d3;
    background: white;
    box-shadow: 0 0 0 3px rgba(41, 168, 211, 0.15);
  }
`;

const ResultsBox = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  max-height: 320px;
  overflow-y: auto;
  background: white;
`;

const EmptyState = styled.div`
  padding: 1rem;
  text-align: center;
  color: #9ca3af;
  font-size: 0.85rem;
`;

const ResultRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.75rem;
  padding: 0.6rem 0.75rem;
  background: white;
  & + & {
    border-top: 1px solid #f1f5f9;
  }
  &:hover {
    background: #fafafa;
  }
`;

const ResultActions = styled.div`
  display: flex;
  gap: 0.4rem;
  flex-shrink: 0;
`;

const ActionBtn = styled.button`
  border: 1px solid #d1d5db;
  background: white;
  color: #1f2937;
  padding: 0.35rem 0.7rem;
  font-size: 0.75rem;
  border-radius: 6px;
  cursor: pointer;
  white-space: nowrap;
  &:hover:not(:disabled) {
    background: #f3f4f6;
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const OpenBtn = styled(ActionBtn)`
  background: #29a8d3;
  color: white;
  border-color: #29a8d3;
  &:hover:not(:disabled) {
    background: #1e8aae;
  }
`;

const ShareLinkBtn = styled(ActionBtn)``;

const ResultText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const ResultEmail = styled.span`
  color: #1f2937;
  font-size: 0.9rem;
  word-break: break-all;
`;

const ResultHint = styled.span`
  color: #6b7280;
  font-size: 0.7rem;
`;

const SignOutBtn = styled.button`
  background: none;
  border: none;
  color: #0066cc;
  cursor: pointer;
  text-decoration: underline;
  margin-top: 0.5rem;
  align-self: center;
  font-size: 0.85rem;
  &:hover {
    color: #004499;
  }
`;
